// apps/web/src/lib/androidBridge.ts
declare global {
    interface Window {
        Android?: {
            callSpaceLibrary?: () => void;
            callPageBox?: () => void;
            pickImage?: (callbackId: string) => void;
            saveImageFromUrl?: (url: string, displayName: string, target: string) => void;
            saveImageFromBase64?: (dataUrl: string, displayName: string, target: string) => void;
            saveAuthTokenJson?: (json: string) => void;
            getAuthTokenJson?: () => string;
            getDeviceInfo?: () => object;
            showToast?: (msg: string) => object;
            clearAuthToken?: () => void;
            callSpecificApp?: (pkgName: string) => void;
        };

        // ✅ Android가 WebView로 결과 전달할 때 호출하는 함수(앱에서 evaluateJavascript로 호출)
        __booktox_onPickImageResult?: (callbackId: string, payloadJson: string) => void;
    }
}

export type AndroidPickImagePayload = {
    ok: boolean;
    temp_url?: string;   // auth-service pick/complete 업로드 후 임시 경로
    avatar_url?: string; // Android가 직접 업로드 후 최종 URL 반환
    base64?: string;     // Android가 base64로 이미지 전달 (data:image/...;base64,...)
    error?: string;
};

type Handler = (callbackId: string, payload: AndroidPickImagePayload) => void;
const handlers = new Set<Handler>();

export function onAndroidPickImageResult(handler: Handler) {
    handlers.add(handler);
    return () => handlers.delete(handler);
}

// 앱이 호출할 글로벌 함수 설치(앱 시작 시 1회만 실행)
export function installAndroidPickImageCallback() {
    if (typeof window === "undefined") return;

    window.__booktox_onPickImageResult = (callbackId: string, payloadJson: string) => {
        let payload: AndroidPickImagePayload;
        try {
            payload = JSON.parse(payloadJson);
        } catch {
            payload = { ok: false, error: "BAD_PAYLOAD" };
        }
        handlers.forEach((h) => h(callbackId, payload));
    };
}

export function androidPickImage(callbackId: string) {
    try {
        window.Android?.pickImage?.(callbackId);
        return true;
    } catch {
        return false;
    }
}

export function androidShowToast(msg: string) {
    try {
        window.Android?.showToast?.(msg);
        return true;
    } catch {
        return false;
    }
}


export function androidCallSpaceLibrary() {
    try {
        window.Android?.callSpaceLibrary?.();
        return true;
    } catch {
        return false;
    }
}

export function androidGetDeviceInfo() {
    try {
        const raw = window.Android?.getDeviceInfo?.();
        if (raw == null) return null;
        const info = typeof raw === 'string' ? JSON.parse(raw) : JSON.parse(JSON.stringify(raw));
        console.log('[androidGetDeviceInfo]', JSON.stringify(info, null, 2));
        return info;
    } catch (e) {
        console.error('[androidGetDeviceInfo] error', e);
        return null;
    }
}

export function androidCallPageBox() {
    try {
        window.Android?.callPageBox?.();
        return true;
    } catch {
        return false;
    }
}

export function androidSaveImageFromBase64(dataUrl: string, displayName: string, target: string = 'gallery'): boolean {
    try {
        window.Android?.saveImageFromBase64?.(dataUrl, displayName, target);
        return true;
    } catch {
        return false;
    }
}

export function androidSaveImageFromUrl(url: string, displayName: string, target: string = 'gallery'): boolean {
    try {
        window.Android?.saveImageFromUrl?.(url, displayName, target);
        return true;
    } catch {
        return false;
    }
}

// window.Android 자체가 없으면(브라우저) false.
//
// 메서드 존재 여부는 typeof로 검사하지 않는다 — addJavascriptInterface로 주입된
// 객체에서는 실제로 호출되는 메서드도 typeof가 "function"으로 안 나온다(단말에서 확인).
// 그래서 그냥 호출하고 예외만 잡는다.
//
// 반환값은 "브리지에 넘겼다"까지만 뜻한다. callSpecificApp이 void라 앱이 실제로
// 떴는지는 알 수 없고, 미설치/실행 실패 안내는 안드로이드 쪽 몫이다.
export function androidCallSpecificApp(pkgName: string): boolean {
    const bridge = window.Android as { callSpecificApp?: (p: string) => void } | undefined;
    if (!bridge) return false;
    try {
        (bridge.callSpecificApp as (p: string) => void)(pkgName);
        return true;
    } catch {
        return false;
    }
}

/* ── Device model helpers ── */
export const SPC_MODEL = 'SPC05008X';
export const SPC_FONT_BOOST = 4;

export function getDeviceModel(): string {
    try {
        const raw = androidGetDeviceInfo();
        const info = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return info?.model ?? '';
    } catch { return ''; }
}

